import { useState, useEffect } from 'react';
import Container from 'components/UI/atoms/Container';
import Text from 'components/UI/atoms/Text';
import Grid from 'components/UI/atoms/Grid';
import Tab from 'components/UI/atoms/Tab';
import Button from 'components/UI/atoms/Button';
import FloatingButton from 'components/UI/atoms/FloatingButton';
import { UserRole, WakeUp } from 'common/interface/props';
import UserAdminDashboard from './UserAdminDashboard';
import UserAdminJobMail from './UserAdminJobMail';
import UserAdminMemberLookup from './UserAdminMemberLookup';
import { USER_ADMIN_CONFIG } from './config/user-admin.config';
import { partnerAuthCheck } from 'components/form/api/partner-admin.api';

interface IUserAdminMyPage {
  solType: WakeUp;
}

export default function UserAdminMyPage({ solType }: IUserAdminMyPage) {
  const config = USER_ADMIN_CONFIG[solType];

  // 모든 Hook은 조건문 이전에 호출되어야 함
  // 연동관리 타입 (조회)
  const [adminType, setAdminType] = useState<string>('조회');
  // 파트너 권한 여부 (멤버조회 탭 노출 판단)
  const [isPartner, setIsPartner] = useState<boolean>(false);
  // 권한
  const [role, setRole] = useState<UserRole | undefined>(undefined);
  // 권한 체크 로딩 상태
  const [isCheckingRole, setIsCheckingRole] = useState<boolean>(true);

  /**
   * @alias 파트너 권한 확인
   * @description 파트너 권한 확인 후 멤버조회 탭 노출 여부 판단
   */
  useEffect(() => {
    const checkPartnerAuth = async () => {
      try {
        const result = await partnerAuthCheck();
        if (result?.success && result?.isPartner) {
          setIsPartner(true);
          setRole(result?.role);
          if (result?.role === 'PARTNER_ST11') {
            setAdminType('멤버조회');
          }
        }
      } catch {
        // 401 등 에러 시 에러 표시 없이 탭 미노출
      } finally {
        setIsCheckingRole(false);
      }
    };
    checkPartnerAuth();
  }, []);

  /**
   * @alias 연동관리 타입 변경 이벤트
   * @description 확장성을 위해 일단 가지고 있음
   */
  const changeAdmintype = (adminType: string) => {
    setAdminType(adminType);
  };

  // config가 없으면 early return
  if (!config) {
    return (
      <Container>
        <Text>지원하지 않는 쇼핑몰입니다.</Text>
      </Container>
    );
  }

  // 권한 확인 중에는 아무것도 보여주지 않음
  // 불필요한 렌더링 방지
  if (isCheckingRole) {
    return (
      <Container>
        <Grid display="flex" justifyContent="center" alignItems="center" height={400}>
          <Text>권한 정보를 확인 중입니다...</Text>
        </Grid>
      </Container>
    );
  }

  return (
    <Container>
      <Grid bottom={10}>
        {/* 타이틀 및 이동링크 */}
        <Grid
          top={40}
          bottom={40}
          display="flex"
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={10}
        >
          <Text fontSize="36px" fontWeight={600} color="#333">
            {config.pageTitle}
          </Text>
          <Grid display="inline-flex" direction="row" gap={10}>
            {config.sellerCenterLinks.map((link, index) => (
              <Button
                key={index}
                variant="outlined"
                color="main"
                width={248}
                height={45}
                display="flex"
                justifyContent="center"
                alignItems="center"
                size="small"
                borderRadius={4}
                href={link.url}
                target="_blank"
                rel="noreferrer"
                style={{
                  width: 'max-content',
                }}
              >
                {link.text}
              </Button>
            ))}
          </Grid>
        </Grid>
        {/* 설명안내 */}
        <Grid color="#f7f7f7" top={30} bottom={30} left={30} right={30} marginBottom={40}>
          {/* 이메일 수신 토글 */}
          <UserAdminJobMail />
          <Grid top={20}>
            <Text fontSize="14px" color="#555" lineHeight={24} fontWeight={500}>
              {config.descriptionText}
            </Text>
            <Text fontSize="14px" fontWeight={600} color="#333" lineHeight={24}>
              {config.highlightText}
            </Text>
            <br />
            <Text fontSize="14px" color="#555" lineHeight={24} fontWeight={500} whiteSpace="pre-line">
              {config.additionalText}
            </Text>
          </Grid>
        </Grid>
        <Tab
          currentTab={adminType}
          color="#333"
          // 어드민 권한 : 조회, 멤버조회
          // 파트너 권한 : 멤버조회
          // 일반 권한 : 조회
          tabNames={
            isPartner && role === 'ADMIN'
              ? ['조회', '멤버조회']
              : isPartner && role === 'PARTNER_ST11'
                ? ['멤버조회']
                : ['조회']
          }
          tabNodes={
            isPartner && role === 'ADMIN'
              ? [
                  <UserAdminDashboard key="조회" solType={solType} />,
                  <UserAdminMemberLookup key="멤버조회" solType={solType} />,
                ]
              : isPartner && role === 'PARTNER_ST11'
                ? [<UserAdminMemberLookup key="멤버조회" solType={solType} />]
                : [<UserAdminDashboard key="조회" solType={solType} />]
          }
          tabSwitchEvent={changeAdmintype}
          tabSwitchValues={
            isPartner && role === 'ADMIN'
              ? ['조회', '멤버조회']
              : isPartner && role === 'PARTNER_ST11'
                ? ['멤버조회']
                : ['조회']
          }
        />
      </Grid>
      {/* 플로팅 문의 버튼 */}
      <FloatingButton
        href="https://plto.com/customer/Inquiry/"
        target="_blank"
        rel="noreferrer"
        ariaLabel="문의하기"
        color="main"
      >
        ?
      </FloatingButton>
    </Container>
  );
}
